import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { AlertTriangle, Building2, Mail, Phone, Plus, Search } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, StatTile, TableShell, Th } from "@/features/power_projects/components/ui";
import { LeadFormModal } from "@/features/leads/components/LeadFormModal";
import {
  STATUS_COLORS,
  useLeadDictionaries,
  useLeadSummary,
  useLeads,
  type LeadFilters,
  type LeadStatus,
} from "@/features/leads/api/leads";

/**
 * Lista leadów.
 *
 * Domyślnie NIE pokazuje zablokowanych i odrzuconych — to ma być lista do
 * dzwonienia, a nie archiwum. Kafelki na górze są jednocześnie licznikiem i
 * filtrem: kliknięcie „Zainteresowani: 6" pokazuje właśnie tych sześciu.
 */

const TILE_STATUSES: { value: LeadStatus; label: string; tone: "default" | "blue" | "warn" | "ok" }[] = [
  { value: "new", label: "Nowe", tone: "default" },
  { value: "contacted", label: "Skontaktowane", tone: "blue" },
  { value: "interested", label: "Zainteresowane", tone: "warn" },
  { value: "converted", label: "W systemie", tone: "ok" },
];

export function LeadsPage() {
  const [filters, setFilters] = useState<LeadFilters>({ ordering: "-created_at" });
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  const { data: dictionaries } = useLeadDictionaries();
  const { data: summary } = useLeadSummary();
  const { data: leads, isLoading } = useLeads({ ...filters, q: search });

  const counts = new Map((summary?.by_status ?? []).map((row) => [row.value, row.count]));
  const toggleStatus = (status: LeadStatus) =>
    setFilters((prev) => ({
      ...prev,
      status: prev.status === status ? "" : status,
      include_blocked: true,
    }));

  return (
    <>
      <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {TILE_STATUSES.map((tile) => (
          <StatTile
            key={tile.value}
            label={tile.label}
            value={counts.get(tile.value) ?? 0}
            tone={filters.status === tile.value ? "danger" : tile.tone}
            hint={filters.status === tile.value ? "filtr włączony" : undefined}
            onClick={() => toggleStatus(tile.value)}
            title="Kliknij, żeby pokazać tylko te leady"
          />
        ))}
        <StatTile
          label="Moje zadania"
          value={summary?.my_open_tasks ?? 0}
          tone={summary?.my_overdue_tasks ? "danger" : "default"}
          hint={summary?.my_overdue_tasks ? `${summary.my_overdue_tasks} po terminie` : undefined}
        />
        <StatTile
          label="Nie kontaktować"
          value={summary?.suppressed ?? 0}
          hint="wpisów na liście"
        />
      </div>

      <div className="card mb-5 flex flex-wrap items-center gap-2 p-3">
        <div className="relative min-w-[15rem] flex-1">
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj po nazwie, NIP, mieście, osobie, mailu lub telefonie…"
            className="input-base pl-8"
          />
        </div>
        <select
          value={filters.status ?? ""}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="input-base sm:w-auto"
        >
          <option value="">Aktywne w lejku</option>
          {(dictionaries?.statuses ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={filters.voivodeship ?? ""}
          onChange={(e) => setFilters({ ...filters, voivodeship: e.target.value })}
          className="input-base sm:w-auto"
        >
          <option value="">Każde województwo</option>
          {(dictionaries?.voivodeships ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={filters.ordering ?? "-created_at"}
          onChange={(e) => setFilters({ ...filters, ordering: e.target.value })}
          className="input-base sm:w-auto"
        >
          <option value="-created_at">Najnowsze</option>
          <option value="last_activity_at">Najdawniej ruszane</option>
          <option value="-last_activity_at">Ostatnio ruszane</option>
          <option value="name">Nazwa A–Z</option>
        </select>
        <label className="section-label flex items-center gap-1.5 whitespace-nowrap">
          <input
            type="checkbox"
            checked={filters.owner === "me"}
            onChange={(e) => setFilters({ ...filters, owner: e.target.checked ? "me" : "" })}
          />
          Moje
        </label>
        <label className="section-label flex items-center gap-1.5 whitespace-nowrap">
          <input
            type="checkbox"
            checked={!!filters.overdue}
            onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })}
          />
          Po terminie
        </label>
        <Button size="sm" onClick={() => setFormOpen(true)}>
          <Plus size={13} className="mr-1 inline" /> Nowy lead
        </Button>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : (leads ?? []).length === 0 ? (
        <div className="card">
          <EmptyState>
            Brak leadów spełniających kryteria.
            <div className="mt-3">
              <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
                <Plus size={13} className="mr-1 inline" /> Dodaj pierwszy lead
              </Button>
            </div>
          </EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Firma</Th>
              <Th>Kontakt</Th>
              <Th>Status</Th>
              <Th>Prowadzący</Th>
              <Th>Zadania</Th>
              <Th>Ostatni ruch</Th>
            </tr>
          </thead>
          <tbody>
            {(leads ?? []).map((lead) => (
              <tr
                key={lead.id}
                className="border-t border-[var(--color-border)] align-top hover:bg-[var(--color-bg-elevated)]"
              >
                <td className="px-3 py-2.5">
                  <Link
                    to={`/leads/${lead.id}`}
                    className="text-sm font-medium text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                  >
                    {lead.name}
                  </Link>
                  <div className="mt-0.5 font-mono text-[11px] text-[var(--color-text-muted)]">
                    {[lead.nip && `NIP ${lead.nip}`, lead.city].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {lead.contractor_name && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-500">
                      <Building2 size={11} /> {lead.contractor_name}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {lead.primary_contact ? (
                    <>
                      <div className="text-[var(--color-text-primary)]">
                        {lead.primary_contact.display_name}
                        {lead.primary_contact.position && (
                          <span className="text-[var(--color-text-muted)]">
                            {" "}
                            · {lead.primary_contact.position}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-[var(--color-text-secondary)]">
                        {lead.primary_contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail size={10} /> {lead.primary_contact.email}
                          </span>
                        )}
                        {lead.primary_contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={10} /> {lead.primary_contact.phone}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <span className="text-[11px] text-[var(--color-text-muted)]">
                      brak osoby kontaktowej
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Badge color={STATUS_COLORS[lead.status]}>{lead.status_label}</Badge>
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">
                  {lead.owner_name ?? "—"}
                </td>
                <td className="px-3 py-2.5">
                  {lead.open_tasks_count === 0 ? (
                    <span className="text-[var(--color-text-muted)]">—</span>
                  ) : (
                    <span
                      className={
                        lead.overdue_tasks_count > 0
                          ? "inline-flex items-center gap-1 font-mono text-[var(--color-primary)]"
                          : "font-mono text-[var(--color-text-secondary)]"
                      }
                    >
                      {lead.overdue_tasks_count > 0 && <AlertTriangle size={11} />}
                      {lead.open_tasks_count}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">
                  {lead.last_activity_at ? (
                    <>
                      {formatDistanceToNow(new Date(lead.last_activity_at), {
                        addSuffix: true,
                        locale: pl,
                      })}
                      {lead.last_activity_by_name && (
                        <div className="text-[11px] text-[var(--color-text-muted)]">
                          {lead.last_activity_by_name}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">nic się nie działo</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      <LeadFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        dictionaries={dictionaries}
      />
    </>
  );
}
